import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import LoginPage from '../pages/auth/LoginPage';
import { BrioButton } from '../components/BrioComponents';
import '../styles/brio-styles.css';
import './styles/qs-theme.css';
import BoqNav, { PROJECT_TAB_IDS } from './components/BoqNav';
import ProjectSelector from './components/ProjectSelector';
import { useBoqProjects } from './hooks/useBoqProjects';
import { useBoqLibrary } from './hooks/useBoqLibrary';
import { useBoqRateLibrary, useBoqRateSources, useVendorDirectory } from './hooks/useBoqRateLibrary';
import ProjectWorkspace from './pages/ProjectWorkspace';
import LibraryPage from './pages/LibraryPage';
import RatesPage from './pages/RatesPage';
import CostIndexPage from './pages/CostIndexPage';
import SdRatesPage from './pages/SdRatesPage';
import TaxonomiesPage from './pages/TaxonomiesPage';
import SourceFilesPage from './pages/SourceFilesPage';
import CustomSubtradeReviewPage from './pages/CustomSubtradeReviewPage';
import ClauseLibraryPage from './pages/ClauseLibraryPage';

const TAB_META = {
  'project-details': { title: 'Project details', subtitle: 'BOQ details and zones for the selected project' },
  bill: { title: 'Bill of Quantities', subtitle: 'Priced line items for the selected project' },
  measurements: { title: 'Measurements', subtitle: 'Take-off measurements feeding the bill quantities' },
  summary: { title: 'Summary', subtitle: 'Rolled-up cost summary for the selected project' },
  versions: { title: 'Versions', subtitle: 'Bill version snapshots — compare, restore and stage history' },
  library: { title: 'Item library', subtitle: 'Versioned items shared across all BOQ projects' },
  rates: { title: 'TD rate library', subtitle: 'Benchmark DD/TD rates harvested from sample BOQs, with full provenance' },
  'sd-rates': { title: 'SD area rates', subtitle: 'Cross-project per-sqm Schematic-Design rates by subtrade, with cost-index-escalated medians' },
  'cost-index': { title: 'Construction cost index', subtitle: 'Yearly escalation index used to inflation-adjust library rates onto a new BOQ pricing date' },
  'source-files': { title: 'Source files', subtitle: 'Every file the library was built from, and whether each bidder\'s rates read as Estimate or Tender' },
  taxonomies: { title: 'Classification taxonomies', subtitle: 'NRM1, IS 1200, CSI MasterFormat, typology, spec-depth, trade-group' },
  'review-subtrades': { title: 'Subtrade template', subtitle: 'Admin editor for the canonical brio_trade taxonomy — rename, set defaults, promote / merge / reject custom proposals' },
  clauses: { title: 'Standard clauses', subtitle: 'Firm-wide inclusions, exclusions, assumptions & qualifications that seed each new project bill' },
};

function BoqApp() {
  const { session, loading, signOut, email } = useAuth();
  const [tab, setTab] = useState('library');
  const [selectedProjectCode, setSelectedProjectCode] = useState(null);
  const [ratesQuery, setRatesQuery] = useState('');
  // Where a Library rate link came from ({ itemCode, description }), so the
  // Rates page can offer a way back to that item. Set only by openRates; any
  // other move between tabs clears it, since the way back would then be stale.
  const [ratesReturn, setRatesReturn] = useState(null);
  // The item the Library should reopen, expand and scroll to when it mounts,
  // cleared by the Library once it has done so.
  const [libraryFocus, setLibraryFocus] = useState(null);

  // Projects load once here — feeds both the header selector and the
  // project-scoped workspace, so there's no double fetch.
  // Nothing loads until someone is signed in. These hooks sit above the login
  // gate, so without this they fetched from the login screen as anon, were
  // refused (Library: "Permission denied") and never fetched again after
  // sign-in; only a page refresh, with the session already stored, recovered.
  // Keyed on the user id so a token refresh does not refetch, while signing
  // out and back in (even as someone else) does.
  const signedIn = !!session?.user?.id;
  const { projects, loading: projectsLoading, upsertBoqMirror, refresh: refreshProjects } = useBoqProjects({ enabled: signedIn });

  // The Library and Rates pages share the same (heavy) item + rate datasets,
  // and the Library cross-links into Rates. Load them once, lazily, the first
  // time the user opens either page, then keep the hooks alive here so the
  // data is cached — switching between the two pages (and following a rate
  // link) is instant instead of re-fetching everything on every mount.
  const [boqDataActivated, setBoqDataActivated] = useState(false);
  useEffect(() => {
    if (tab === 'library' || tab === 'rates') setBoqDataActivated(true);
  }, [tab]);

  const boqDataEnabled = boqDataActivated && signedIn;
  const library = useBoqLibrary({ enabled: boqDataEnabled });
  const rateApi = useBoqRateLibrary({ enabled: boqDataEnabled });
  const sourceApi = useBoqRateSources({ enabled: boqDataEnabled });
  const vendorDir = useVendorDirectory({ enabled: boqDataEnabled });

  const navigate = (target) => {
    setTab(target);
    if (target !== 'rates') setRatesQuery('');
    setRatesReturn(null);
  };

  // Picking a project in the header. If we're parked on a global tab, jump to
  // the project's details; if already on a project tab, stay put and just swap
  // the underlying project so context is preserved.
  const handleSelectProject = (code) => {
    setSelectedProjectCode(code);
    if (!PROJECT_TAB_IDS.includes(tab)) setTab('project-details');
  };

  const openRates = (query, from = null) => {
    setRatesQuery(query || '');
    setRatesReturn(from);
    setTab('rates');
  };

  // Back from Rates to the Library item the rate link was followed from. The
  // Library unmounts while Rates is shown, so it is told which item to reopen.
  const backToLibrary = () => {
    if (ratesReturn?.itemCode) setLibraryFocus(ratesReturn.itemCode);
    setRatesReturn(null);
    setRatesQuery('');
    setTab('library');
  };

  useEffect(() => {
    document.title = 'BOQ Pro — BRIO';
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--brio-text-muted)' }}>
        Loading…
      </div>
    );
  }

  if (!session) {
    return <LoginPage />;
  }

  const meta = TAB_META[tab] || TAB_META.library;

  return (
    <div className="boq-app" style={{ width: '100%', maxWidth: '100%', padding: '0 24px', boxSizing: 'border-box' }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1.5rem',
          padding: '0.6rem 0',
          borderBottom: '1px solid var(--brio-border, #e5e5e5)',
          fontFamily: 'Raleway, sans-serif',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, color: 'var(--brio-black, #1a1a1a)', whiteSpace: 'nowrap' }}>
            {meta.title}
          </h1>
          <span style={{ fontSize: '0.9rem', fontWeight: 300, fontStyle: 'italic', color: 'var(--brio-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {meta.subtitle}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexShrink: 0 }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--brio-text-muted)' }}>{email}</span>
          <BrioButton variant="secondary" onClick={signOut}>Sign out</BrioButton>
          <span style={{ width: 1, height: '1.4rem', background: 'var(--brio-border-subtle, #e5e5e5)' }} />
          <ProjectSelector
            projects={projects}
            loading={projectsLoading}
            selectedCode={selectedProjectCode}
            onSelect={handleSelectProject}
          />
        </div>
      </header>
      <BoqNav current={tab} onChange={navigate} projectSelected={!!selectedProjectCode} />
      <div style={{ padding: '0 0 2rem' }}>
        {PROJECT_TAB_IDS.includes(tab) && (
          <ProjectWorkspace
            tab={tab}
            projectCode={selectedProjectCode}
            projects={projects}
            projectsLoading={projectsLoading}
            upsertBoqMirror={upsertBoqMirror}
            refreshProjects={refreshProjects}
          />
        )}
        {tab === 'library' && (
          <LibraryPage
            library={library}
            onOpenRates={openRates}
            focusItemCode={libraryFocus}
            onFocusConsumed={() => setLibraryFocus(null)}
          />
        )}
        {tab === 'rates' && (
          <RatesPage
            initialQuery={ratesQuery}
            returnTo={ratesReturn}
            onBack={backToLibrary}
            library={library}
            rateApi={rateApi}
            sourceApi={sourceApi}
            vendorDir={vendorDir}
          />
        )}
        {tab === 'sd-rates' && <SdRatesPage />}
        {tab === 'cost-index' && <CostIndexPage />}
        {tab === 'source-files' && <SourceFilesPage onRatesChanged={boqDataActivated ? rateApi.refresh : undefined} />}
        {tab === 'taxonomies' && <TaxonomiesPage />}
        {tab === 'review-subtrades' && <CustomSubtradeReviewPage />}
        {tab === 'clauses' && <ClauseLibraryPage />}
      </div>
    </div>
  );
}

export default BoqApp;
